import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { CalendarMonth } from '@mui/icons-material';
import styled from '@emotion/styled';
import BaseWidget from './BaseWidget';
import AccountTabCalendar from '../components/AccountTabCalendar';
import { useFetchQueue } from '../hooks/useFetchQueue';
import { requestGetTasks } from '../redux/sagas/requests/task';

const EmptyState = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 160,
    color: theme.palette.text.secondary,
}));

const EmptyStateIcon = styled(CalendarMonth)(({ theme }) => ({
    fontSize: 40,
    marginBottom: theme.spacing(1),
    opacity: 0.5,
}));

const TeamCalendarWidget = ({ widgetId, onRemove }) => {
    const { data: tasks, isLoading, error } = useFetchQueue(widgetId, {
        apiCall: () => requestGetTasks(0, 500, 'open'),
        cacheKey: 'team-calendar-tasks',
        transform: (response) => response,
    });

    const items = useMemo(() => {
        const list = Array.isArray(tasks) ? tasks : [];
        return list
            .filter((t) => !t.completedDate && t.startDate && t.dueDate)
            .map((t) => ({
                _id: `task-${t._id}`,
                name: t.name,
                startDate: t.startDate,
                dueDate: t.dueDate,
                status: t.status,
                account: t.account ? { name: t.account.name, slug: t.account.slug } : undefined,
                responsible: t.responsible ? {
                    _id: t.responsible._id,
                    fullName: t.responsible.fullName,
                    avatar: t.responsible.avatar,
                } : undefined,
                _raw: t,
            }));
    }, [tasks]);

    const getLink = (item) => {
        const raw = item._raw;
        const slug = raw?.account?.slug || raw?.account?.name;
        const projectId = raw?.project?._id;
        if (!slug || !projectId) return null;
        return `/account/${slug}/project/${projectId}`;
    };

    const hasItems = items.length > 0;

    return (
        <BaseWidget
            title="Active Tasks Calendar"
            widgetId={widgetId}
            onRemove={onRemove}
            isLoading={isLoading}
            error={error}
        >
            {!hasItems && !isLoading ? (
                <EmptyState>
                    <EmptyStateIcon />
                    <Typography variant="body2">No active tasks scheduled</Typography>
                </EmptyState>
            ) : (
                <AccountTabCalendar
                    projects={items}
                    getLink={getLink}
                    emptyMessage="No active tasks in this date range"
                    stickyHeader
                />
            )}
        </BaseWidget>
    );
};

export default TeamCalendarWidget;
